import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Grid, Icon, Link, makeStyles, Tooltip } from '@material-ui/core';
import TanStackTable from '../../atoms/table/TanStackTable';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import ErrorIcon from '@material-ui/icons/Error';
import { generateWidgetTransactionWithAI, handleSnackBar, invokeGetService, updateAiAssistant } from '../../../store/global/actions';
import { getDetailsFromUrl } from '../../../services/blueprint';
import LoaderWithProgress from '../../atoms/loader/LoaderWithProgress';
import useConstructsNavigation from '../../../hooks/useConstructsNavigation';
import { findElementById, modifyViewsSchemaWithMappedFields, sortViewSchema, validateSingleView } from '../../../utils/DashboardBuilder';
import TransactionManager from '../../../services/transactionManager';
import MessageSchemaModal from '../modals/messageSchemaModal';
import { buildMessageStructure } from '../../../utils/transactionUtils';
import { getAIWidgetTransactionDetails, getReRender } from '../../../store/global/selectors';
import { findTriggerEvent } from '../../../utils/commonUtils';
import { widgetGroups } from '../../../config/common';
import { setView } from '../../../store/views/actions';
import { getViews } from '../../../store/views/selectors';
import { Alert } from '@material-ui/lab';
import { StarsIcon } from '../icons';

const useStyles = makeStyles((theme) => ({
    root: {
        marginTop: "40px",
        paddingLeft: "10px", 
        paddingRight: "10px",
        textAlign: 'center'
    },
    editIcon: {
        fontSize: 20,
        cursor: "pointer"
    },
    addIcon: {
        fontSize: 24,
        cursor: "pointer"
    },
    elementName: {
        fontWeight: '500',
        paddingRight: '8px'
    },
    validationWarning: {
        color: '#cb4d4d',
        paddingLeft: '10px'
    },
    itemDescription: {
        fontSize: 12
    },
    descOffset: {
        paddingLeft: '22px'
    },
    targetClusterlet: {
        paddingTop: 5,
        fontSize: '12px'
    },
    responseMessage: {
        cursor: 'pointer',

        textUnderlineOffset: '4px',
        textDecorationThickness: '1px',
        textDecorationLine: 'underline',

        color: theme.palette.text.primary,

        '&:hover': {
            color: theme.palette.primary.main // same as Link color
        }
    },
    underLine: {
        textUnderlineOffset: '4px',
        textDecorationThickness: '1px',
        textDecorationLine: 'underline',
    },
    invalidMappingIcon: {
        marginLeft: 4,
        fontSize: 14,
        color: "#fbc02d", // warning yellow
        verticalAlign: "middle",
        cursor: "pointer"
    },
    invalidTransactionIcon: {
        marginLeft: 4,
        fontSize: 14,
        color: "#eb5f5f",
        verticalAlign: "middle",
        cursor: "pointer"
    }

}));

const DataSourceMapping = ({generateTransactionWithAI, isAIGeneratingTransaction}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { navigateToConstruct } = useConstructsNavigation();
    const { blueprintId, entityId, slug } = getDetailsFromUrl();
    const reRender = useSelector(getReRender);

    const [viewValidationWarnings, setVaildationWarnings] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [clusterlets, setClusterlets] = useState([]);
    const [events, setEvents] = useState([]);
    const [messages, setMessages] = useState([])
    const [loading, setLoading] = useState(true);
    const [messageChildren,  setMessageChildren] = useState([]);
    const [isViewMapped, setIsViewMapped] = useState(true);
    const [quickViewMessages, setQuickViewMessages] = useState([])


    const selectedView = useSelector(getViews);
    const aiWidgetTransactionDetails = useSelector(getAIWidgetTransactionDetails)
    

    const onEditField = (item) => {
      navigateToConstruct({
          mainEntityId: entityId, 
          mainEntityType: "view", 
          entityId: item.id || item._id, 
          entityType: item.category === "ui" ? "widget" : item.category, 
          entityItem: item
      })
    }

  const handleQuickViewMessages = (ev, eventData) => {
    const message = messageChildren?.find(
        item => item.eventId === eventData._id
    );
    if (!message) return;

    const container = document.getElementById("datasource_mapping_table");
    if (!container) {
        console.warn("datasource_mapping_table not found");
        return;
    }

    const rect = container.getBoundingClientRect();

    // ✅ Correct for page scroll (NOT container scroll)
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top - window.scrollY;

    setQuickViewMessages(prev => {
        if (
            prev.findIndex(
                item => item.message?.eventId === message.eventId
            ) === -1
        ) {
            return [...prev, { x, y, message }];
        }
        return prev;
    });
};



  const handleClickHyperlink = (id, type, viewResponseSchema=false) => {
    if(type === "Transaction"){
        if(viewResponseSchema){
            navigateToConstruct({
                mainEntityId: id , 
                mainEntityType: "transaction", 
                entityId: viewResponseSchema,
                entityType: "event",
                tabOnFocus: 1
            })

        }else{
            navigateToConstruct({
                mainEntityId: id , 
                mainEntityType: "transaction", 
            })
        }
    }else{
        const clusterlet = clusterlets.find(item => item._id.toString() === id)
        navigateToConstruct({
            entityId: id, 
            entityType: clusterlet?.type === 2 ? "clusterlet" : "analytics", 
        })
    }
  }

  const getWidgetType = (type) => {
    let allWidgetItems = []
    widgetGroups?.forEach(group => {
        group.widgets?.forEach(item => allWidgetItems.push(item) )
    }) 
    const title = allWidgetItems?.find(item => item?.value === type)?.title || "Widget" ;
    return title;
}

  const getViewElementType = (item) => {
    return item.category === "view" ? "View" : item.category === "ui" ? getWidgetType(item.type) : "Section" 
  }

  const generateIndividualTransaction = (selectedEntity) => {
    dispatch(generateWidgetTransactionWithAI({
        status: 'loading',
        entity: selectedEntity
    }))
  }

  const checkMappingisValid = (item) => {
    try {
        const parentItem = findElementById(tableData, item.parentId)
        if(parentItem) {
            if(hasMapping(parentItem)){
                return false
            }else{
                return checkMappingisValid(parentItem)
            }
        }
        return true;

    } catch (error) {
        return true;
    }
  }

  const getTableColumns = () =>  {
    return [
        {
            "accessorKey": "name",
            "header": "View Elements",
            cell: ({ row, getValue }) => {
                const mappingRequired = viewValidationWarnings?.find(item => item.id === row.original.id || item.id === row.original._id)
                const hasChildren = row.original?.subRows?.length > 0 ? true : false;
                const isSectionWithNoChildren = !hasChildren && row.original?.category === "section"
                const isSection = row.original?.category === "section"
                return (
                    <>
                    <div style={{ display: 'flex', alignItems: 'center', paddingLeft: ` ${row.depth*2}rem` }}>
                        {isSectionWithNoChildren ? (
                            <Tooltip title="This section has no widgets" arrow placement="top">
                                <span style={{ display: "inline-flex", cursor: "default" }}>
                                <ArrowRightIcon
                                    id="expand_row"
                                    style={{
                                        opacity: 0.7,
                                        pointerEvents: "none",
                                        color: "rgba(0, 0, 0, 0.38)", // MUI disabled color
                                    }}
                                />
                                </span>
                            </Tooltip>
                        ) : (
                            row.getCanExpand() ? (
                                <>
                                    {row.getIsExpanded()
                                        ? <ArrowDropDownIcon id="collapse_row" onClick={row.getToggleExpandedHandler()} style={{ cursor: "pointer" }} />
                                        : <ArrowRightIcon id="expand_row" onClick={row.getToggleExpandedHandler()} style={{ cursor: "pointer" }} />
                                    }
                                </>
                            ) : (
                                ""
                            )
                        )}
                        
                        

                        <span className={`view-element-name view-element-${row.original.name} ${classes.elementName}`}>{row.original.name}</span> 
                        {mappingRequired ? 
                            <div>
                                <Link id={"add_mapping_link"} className={classes.validationWarning} href={"#"} underline="always" onClick={() => onEditField(row.original)}> Mapping is required </Link> 
                                {
                                    row.original.category === "ui" &&
                                    <span 
                                        onClick={() => generateIndividualTransaction(row.original)}
                                        onMouseOver={(event) => {
                                            if((aiWidgetTransactionDetails && aiWidgetTransactionDetails?.status === 'idle') || !aiWidgetTransactionDetails){
                                                event.target.style.textDecoration = 'underline'
                                            }
                                        }}
                                        onMouseOut={(event) => event.target.style.textDecoration = 'none'}
                                        style={{
                                            fontSize: '12px', 
                                            color: 'rgb(0, 123, 255)', 
                                            cursor: 'pointer',
                                            marginLeft: '0.5rem'
                                        }}>
                                           {
                                            aiWidgetTransactionDetails && aiWidgetTransactionDetails?.status === "loading" && aiWidgetTransactionDetails?.entity.id === row.original.id &&
                                            <span style={{
                                                display: 'inline-block',  
                                                width: '14px',
                                                height: '14px',
                                                marginLeft: '8px',
                                                verticalAlign: 'middle',
                                                backgroundImage: `url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22%3E%3Ccircle cx=%2250%22 cy=%2250%22 r=%2240%22 stroke=%22%23007bff%22 stroke-width=%2210%22 fill=%22none%22 stroke-dasharray=%22150 100%22%3E%3CanimateTransform attributeName=%22transform%22 type=%22rotate%22 from=%220 50 50%22 to=%22360 50 50%22 dur=%221s%22 repeatCount=%22indefinite%22/%3E%3C/circle%3E%3C/svg%3E')`,
                                                backgroundSize: 'contain',
                                                backgroundRepeat: 'no-repeat',
                                                marginRight: '4px'
                                              }}
                                            ></span>
                                           }
                                        <span style={
                                            (aiWidgetTransactionDetails && aiWidgetTransactionDetails?.status === "loading") ?
                                            {
                                                cursor: 'not-allowed',
                                                pointerEvents: 'none',
                                                opacity: '0.5'
                                            } :
                                            {
                                                cursor: 'pointer',
                                                pointerEvents: 'all',
                                                opacity: '1'
                                            }
                                        }>Generate with AI</span>
                                    </span>
                                }
                            </div>
                            : ""
                        } 

                    </div>
                    <div className={`${classes.itemDescription} view-element-description`} style={{ display: 'flex', alignItems: 'center', paddingLeft: `${row.depth * 2 }rem` }}>
                       <div className={hasChildren || isSection ? classes.descOffset : ""}> {row.original.desc || ""}  </div> 
                    </div>
                    </>
                )
            },
        },
        {
            "accessorKey": "element_type",
            "header": "Element Type",
            cell: ({ row, getValue }) => {
                return getViewElementType(row.original) ;
            }
        },
        {
            "accessorKey": "datasource",
            "header": "Data Source",
            cell: ({ row, getValue }) => {
                const mapping = getMappingData(row.original.mapping)
                const targetClusterlet = mapping?.targetClusterlet || mapping?.instance || "";
                const isValidTransaction = mapping?.isValidTransaction;
                const isValidMapping = mapping ? checkMappingisValid(row.original) : true; 

                return mapping ? (
                    isValidTransaction ?  
                    <>
                        <div id={"datasource_name"}>
                            <Tooltip title={`Open ${mapping?.type || ""}`} arrow placement="top"> 
                                <Link className={classes.underLine} id={"datasource_link"} href="#" data-ui-tour-id={`view-${mapping?.type === "Clusterlet" ? "clusterlet" : "transaction" }`} onClick={(e) => {e.preventDefault();  handleClickHyperlink(mapping?.id, mapping?.type)}}> {mapping.datasource} </Link>
                            </Tooltip>

                            {!isValidMapping && 
                                <Tooltip title="This mapping will not take effect because the parent element already has an active mapping." arrow placement="top">
                                    <ErrorIcon id={"invalid_mapping_warning"} className={classes.invalidMappingIcon} />
                                </Tooltip>
                            }
                        </div>
                        <div id={"target_cluterlet"} className={classes.targetClusterlet}> {targetClusterlet} </div> 
                    </>
                    : <>
                        <div>
                            <Tooltip title={`Open Transaction`} arrow placement="top"> 
                                <Link className={classes.underLine} id={"datasource_link"} href="#" data-ui-tour-id={`view-transaction`} onClick={(e) => {e.preventDefault();  handleClickHyperlink(mapping?.id, mapping?.type)}}> {mapping.datasource} </Link>
                            </Tooltip>
                            <Tooltip title="This transaction is invalid" arrow placement="top">
                                <ErrorIcon id={"invalid_transaction_icon"} className={classes.invalidTransactionIcon} />
                            </Tooltip>
                        </div>
                    </>
                ) : "";
            }
        },
        {
            "accessorKey": "response_structure",
            "header": "Response Message",
            cell: ({ row, getValue }) => {
                const mapping = getMappingData(row.original.mapping);
                const isValidTransaction = mapping?.isValidTransaction;
                return isValidTransaction && mapping?.type === "Transaction" && mapping?.responseEvent ? (
                    <Tooltip title="View Message" arrow placement="top">
                        <span id="view_response_messsage" className={classes.responseMessage} data-ui-tour-id="view-message-structure" onClick={(ev) => handleQuickViewMessages(ev, mapping?.responseEvent)}>{mapping.message?.name || ""}</span>
                    </Tooltip>
                ) : "";
            }
        },
        {
            "accessorKey": "mapping_field",
            "header": "Field",
            cell: ({ row, getValue }) => {
                const mapping = getMappingData(row.original.mapping)
                const isValidTransaction = mapping?.isValidTransaction;
                return isValidTransaction && mapping?.field ? mapping?.field : "";
            }
        },
        {
            "accessorKey": "mapping_type",
            "header": "Mapping Type",
            cell: ({ row, getValue }) => {
                const mapping = getMappingData(row.original.mapping)
                return mapping?.type || "";
            }
        },
        {
            "accessorKey": "action",
            "header": "",
            cell: ({ row, getValue }) => {

                const disableMappingButton = row.original?.viewCopy ? true : false;
                const mapping = getMappingData(row.original.mapping);

                const tooltipTitle = disableMappingButton
                                    ? "Inherited widgets are configured in the parent schema. Edit the parent to change this mapping"
                                    : mapping
                                    ? "Edit Mapping"
                                    : "Add Mapping";

                return (
                <Box>
                    <Tooltip title={tooltipTitle} arrow placement='top'>
                    <span> {/* Needed because disabled elements don't trigger tooltip */}
                        <Icon
                            onClick={!disableMappingButton ? () => onEditField(row.original) : undefined}
                            style={{
                                opacity: disableMappingButton ? 0.5 : 1,
                                pointerEvents: disableMappingButton ? "none" : "auto",
                                cursor: disableMappingButton ? "not-allowed" : "pointer"
                            }}
                        >
                        {mapping ? (
                            <EditIcon
                                id="edit_mapping"
                                data-ui-tour-id="edit-datasource-mapping-btn"
                                className={classes.editIcon}
                                color="primary"
                            />
                        ) : (
                            <AddIcon
                                id="add_mapping"
                                data-ui-tour-id="add-datasource-mapping-btn"
                                className={classes.addIcon}
                                color="primary"
                            />
                        )}
                        </Icon>
                    </span>
                    </Tooltip>
                </Box>
                );
            },
        }
    ]
  }

const findSelectedMessage = ({responseEvents, selectedField}) => {
    try {
        if(responseEvents?.length === 1 || selectedField === ""){
            const message = messages.find(item => item._id.toString() === responseEvents[0]?.message)
            return {responseEvent:  responseEvents[0], message: message};
        }else{

            const message = messages.find(item => item._id.toString() === responseEvents[0]?.message)
            return {responseEvent:  responseEvents[0], message: message};
        }

    } catch (error) {
        console.error("Finding selected message failed. ", error)
        return {responseEvent:  null, message: null}; 
    }
}

const getMappingData = (data) => {
    try {
        if(data === undefined || data === null) return;
        if(data.transaction === undefined || data.transaction === "select" || data.transaction === null) return;

        const transaction = transactions.find(item => item._id.toString() === data.transaction)
        if(transaction){
            const triggeringEvent = findTriggerEvent({transaction, events, clusterlets});
            const targetClusterlet = triggeringEvent ? clusterlets?.find(item => item._id.toString() === triggeringEvent.dst) : null;
            const transactionManager = new TransactionManager({ clusterlets, transactions, events })
            const instance = data.instance || "";
            let responseEvents = transactionManager?.generateAllResponseEvents(transaction._id) || [];

            const {responseEvent, message} = findSelectedMessage({responseEvents, selectedField: data?.field || ""})

            return {
                type: "Transaction",
                isValidTransaction: triggeringEvent ? true : false,
                datasource: transaction.name,
                targetClusterlet: instance !== "" ? `${targetClusterlet?.name}/${instance}` : (targetClusterlet?.name || ""),
                field: data?.field || "",
                id: data.transaction,
                responseEvent: responseEvent,
                message: message
            }
        }else{
            const clusterlet = clusterlets?.find(item => item._id.toString() === data.transaction)
            if(clusterlet === null || clusterlet === undefined){
                console.error(`Cannot find mapping info using transactin id '${data.transaction}'`)
                return null;
            }
            return {
                type: "Clusterlet",
                isValidTransaction: true,
                datasource: clusterlet.name,
                instance: data?.instance || "",
                field: data?.field || "",
                id: data.transaction
            }
        }
       
    } catch (error) {
        console.error("Finding mapping data failed. ", error);
        return null;
    }
}

const hasMapping = (entity) => {
    const mapping = entity?.mapping;
    return (
      mapping?.source === "transaction" &&
      mapping?.transaction &&
      mapping.transaction !== "select"
    );
};

const getTableData = (entries, parentId) => {
    try {
        return entries
        .map(item => {
                if (item.view_schema && item.view_schema?.length > 0) {
                    item.children = getTableData(item.view_schema, item._id)
                }

                if (item?.category === "section" && item.children && item.children?.length > 0) {
                    const filteredWidgets = item.children?.filter(item => item.category === "ui")
                    item.children = getTableData(filteredWidgets, item.id)
                }
                const obj = {
                    ...item,
                    id: item._id || item.id,
                    parentId: parentId || ""
                };
                if(item?.category === "view" || item?.category === "section"  ){
                     obj.subRows = item.children && item.children.length > 0 ? item.children : undefined;
                }
                return obj;
            });


    } catch (error) {
        console.error(`Generating fields for the table failed. ${error}`);
        return [];
    }
}

const handleSchemaIcons = (schema) => {
    try {
        schema.forEach(section => {
            section?.children?.forEach(widget => {
                if (widget.type === "counter" && widget.customicon) {
                    const assetUrl = window.location.origin;

                    if (widget?.iconSource === 'custom' || widget?.isCustomIcon) {
                        const assetDirectoryName = `${blueprintId}_${slug}`;
                        widget.iconsrc = `${assetUrl}/blueprintAssets/${assetDirectoryName}/${widget.customicon}`;
                    } else if (widget?.iconSource === 'library') {
                        widget.iconsrc = `${assetUrl}/cached_icons/${widget.customicon}`;
                    } else {
                        widget.iconsrc = `${assetUrl}/node_icons/${widget.customicon}.svg`;
                    }
                }
            })
        })

        return schema;

    } catch (error) {
        console.error("Handling icons in schema failed. ", error);
        return schema;
    }
}


const loadViewData = async ({ showLoader = false } = {}) => {
    try {
        if (showLoader) setLoading(true);

        const { status, data } = await dispatch( invokeGetService('getDashboardBuilder', [entityId]) );

        if (!status) {
            dispatch(handleSnackBar({
                open: true,
                message: "Something went wrong",
                severity: 'error'
            }));
            return;
        }

        const { view, clusterlets, transactions, events, messages, sections, fields, assets } = data;


        let modifiedSchema = modifyViewsSchemaWithMappedFields({ schema: view?.view_schema, clusterlets, transactions, events, messages, sections, fields });
        modifiedSchema = handleSchemaIcons(modifiedSchema);
        view.view_schema = sortViewSchema(modifiedSchema);


        dispatch(setView(view));
        
        setClusterlets(clusterlets);
        setTransactions(transactions);
        setEvents(events);
        setMessages(messages);


        const mainTransactions = transactions.filter(item => item.type === 7) || [];
        const structuredMessages = buildMessageStructure({
            transactions: mainTransactions,
            allEvents: events,
            allTransactions: transactions,
            allMessages: messages,
            allFields: fields
        });

        setMessageChildren(structuredMessages);
        

    } catch (error) {
        console.error("Error when fetching dashboard builder:", error);
        dispatch(handleSnackBar({
            open: true,
            message: "Something went wrong",
            severity: 'error'
        }));
    } finally {
        if (showLoader) setLoading(false);
    }
};

    // Initial load → loader visible
    useEffect(() => {
        loadViewData({ showLoader: true });
    }, [entityId]);


    useEffect(() => {
        setVaildationWarnings(validateSingleView(selectedView));
        setIsViewMapped(validateSingleView(selectedView)?.length === 0);
        setTableData(getTableData([{ ...selectedView, category: "view" }], null));
    }, [selectedView, reRender])

    // Re-render refresh → NO loader
    useEffect(() => {
        if (!loading && !isAIGeneratingTransaction) {
            loadViewData({ showLoader: false });
        }
    }, [reRender, isAIGeneratingTransaction]);


    const generateDataTransactionWithAI = async (e) => {
        e.preventDefault();
        const result = await generateTransactionWithAI();
        dispatch(updateAiAssistant({loading: false}));
        if (result.status === true) {
            setIsViewMapped(true);
        }
    }


  return (
    <>
        {loading ? (
            <LoaderWithProgress size={20} isContent={true} styleRoot={{ marginTop: 'calc((100vh - 64px)/2)' }} />
        ): (
        <div id={"datasource_mapping_container"} className={classes.root}>
            <Grid id={"datasource_mapping_table_div"} className={classes.mappingTableContainer} container direction={"row"} spacing={3}>
            {
                quickViewMessages.filter(item => item?.message).map(item => {
                    return ( <MessageSchemaModal setQuickViewMessages={setQuickViewMessages} key={item?.message?.eventId} {...item} /> )
                })
            }

            {!isViewMapped && <Grid item xs={12} style={{padding: "12px 0px"}}>
                            <Alert severity="warning" >
                                This view doesn't have data transactions associated.{" "}
                                <Link
                                    href="#"
                                    color="primary"
                                    onClick={!isAIGeneratingTransaction ? generateDataTransactionWithAI : undefined}
                                    style={{
                                        marginLeft: 6,
                                        pointerEvents: isAIGeneratingTransaction ? "none" : "auto",
                                        textDecoration: isAIGeneratingTransaction ? "none" : "underline",
                                        color: isAIGeneratingTransaction ? "#9e9e9e" : undefined,
                                        cursor: isAIGeneratingTransaction ? "default" : "pointer",
                                    }}
                                >
                                    {isAIGeneratingTransaction ? "Creating with AI.." : "Create with AI"}
                                </Link>
                            </Alert>
                        </Grid>}
                <TanStackTable
                    getTableColumns={getTableColumns}
                    data={tableData}
                    tableId={"datasource_mapping_table"}
                    enableRowMove={false}
                    onRowMove={false}
                    expandAllByDefault={true}
                    textAlign={"left"}
                />
            </Grid>
        </div> )
        }
        
    </>
  )
}

export default DataSourceMapping